import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { ErrorPanel } from "../../shell/ErrorPanel";
import { HierarchyApi } from "../../admin/hierarchy/api";
import { VoterIntelApi, VotersApi, type VoterCount, type VoterScopeLevel } from "./api";

/**
 * Voter reports: registered vs ours, plus expected (intel estimate) vs
 * actual (latest count) comparisons. The screen is a side-by-side view —
 * a level filter on top, charts and tables underneath.
 *
 * Caller can optionally pass a fixed `period` to coordinate with sibling
 * tabs (Summary / Map). When omitted we keep the original local-state
 * picker so this component still works standalone.
 */
export function VoterReports({ period: externalPeriod }: { period?: string } = {}) {
  const [level, setLevel] = useState<VoterScopeLevel>("district");
  const [localPeriod, setLocalPeriod] = useState<string>(String(new Date().getFullYear()));
  const period = externalPeriod ?? localPeriod;
  const setPeriod = externalPeriod ? () => {} : setLocalPeriod;

  const counts = useQuery({
    queryKey: ["voter-counts", level, period],
    queryFn: () => VotersApi.list({ scopeLevel: level, period }),
  });
  // 2021 is the historical ECZ baseline — always loaded alongside the selected
  // period so the charts can render a 2021 vs {period} vs ours comparison.
  const counts2021 = useQuery({
    queryKey: ["voter-counts", level, "2021"],
    queryFn: () => VotersApi.list({ scopeLevel: level, period: "2021" }),
    enabled: period !== "2021",
  });
  const intel = useQuery({
    queryKey: ["voter-intel", level, period],
    queryFn: () => VoterIntelApi.list({ scopeLevel: level === "pollingStation" ? "branch" : level, period }),
  });
  const districts = useQuery({ queryKey: ["districts"], queryFn: HierarchyApi.listDistricts });
  const constituencies = useQuery({ queryKey: ["all-constituencies"], queryFn: HierarchyApi.listConstituencies });

  // Build a lookup of scopeId → name so the charts use human-readable labels
  const nameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const d of districts.data?.items ?? []) m.set(d.id, d.name);
    for (const c of constituencies.data?.items ?? []) m.set(c.id, c.name);
    return m;
  }, [districts.data, constituencies.data]);

  // Bar chart data: per-scope 2021 baseline vs {period} registered vs {period} ours.
  // Joined on scopeId so missing series are simply 0 in that row.
  const barData = useMemo(() => {
    const reg2021 = new Map<string, number>();
    for (const c of counts2021.data?.items ?? []) reg2021.set(c.scopeId, c.registered);
    return (counts.data?.items ?? []).map((c: VoterCount) => ({
      name: nameById.get(c.scopeId) ?? c.scopeId,
      registered2021: reg2021.get(c.scopeId) ?? 0,
      registered: c.registered,
      ours: c.ours,
      gap: c.registered - c.ours,
    }));
  }, [counts.data, counts2021.data, nameById]);

  // Aggregate roll-up across visible scopes
  const totals = useMemo(() => {
    const t = barData.reduce(
      (acc, r) => ({
        registered: acc.registered + r.registered,
        ours: acc.ours + r.ours,
      }),
      { registered: 0, ours: 0 },
    );
    return {
      registered: t.registered,
      ours: t.ours,
      gap: t.registered - t.ours,
      pct: t.registered > 0 ? Math.round((t.ours / t.registered) * 1000) / 10 : 0,
    };
  }, [barData]);

  // Pie of confidence levels across intel records
  const confidenceMix = useMemo(() => {
    const m = new Map<string, number>();
    for (const i of intel.data?.items ?? []) {
      m.set(i.confidence, (m.get(i.confidence) ?? 0) + 1);
    }
    return Array.from(m.entries()).map(([name, value]) => ({ name, value }));
  }, [intel.data]);

  // District-level voter counts joined with province metadata, used for the
  // province-grouped clustered bar + pie. Only meaningful at district level
  // since that's where seeded data lives; we still aggregate when the user
  // is filtered to district level.
  const districtCounts = useQuery({
    queryKey: ["voter-counts", "district", period],
    queryFn: () => VotersApi.list({ scopeLevel: "district", period }),
  });
  // District-level 2021 baseline for the province aggregation.
  const districtCounts2021 = useQuery({
    queryKey: ["voter-counts", "district", "2021"],
    queryFn: () => VotersApi.list({ scopeLevel: "district", period: "2021" }),
    enabled: period !== "2021",
  });

  const provinceAggregates = useMemo(() => {
    const districtToProvince = new Map<string, string>();
    for (const d of districts.data?.items ?? []) {
      const prov = (d as unknown as { province?: string }).province;
      if (prov) districtToProvince.set(d.id, prov);
    }
    const agg = new Map<string, { name: string; registered2021: number; registered: number; ours: number }>();
    for (const c of districtCounts.data?.items ?? []) {
      const prov = districtToProvince.get(c.scopeId);
      if (!prov) continue;
      const cur = agg.get(prov) ?? { name: prov, registered2021: 0, registered: 0, ours: 0 };
      cur.registered += c.registered;
      cur.ours += c.ours;
      agg.set(prov, cur);
    }
    for (const c of districtCounts2021.data?.items ?? []) {
      const prov = districtToProvince.get(c.scopeId);
      if (!prov) continue;
      const cur = agg.get(prov) ?? { name: prov, registered2021: 0, registered: 0, ours: 0 };
      cur.registered2021 += c.registered;
      agg.set(prov, cur);
    }
    return Array.from(agg.values()).sort((a, b) => b.registered - a.registered);
  }, [districtCounts.data, districtCounts2021.data, districts.data]);

  const provinceShare = useMemo(
    () => provinceAggregates.map((p) => ({ name: p.name, value: p.registered })),
    [provinceAggregates],
  );

  // Cycled palette for province-share pie. Order doesn't matter; we just
  // want each slice to be visually distinct.
  const PROVINCE_COLORS = [
    "#2563eb", "#dc2626", "#16a34a", "#f59e0b", "#7c3aed",
    "#0891b2", "#db2777", "#ea580c", "#65a30d", "#475569",
  ];

  return (
    <div>
      <header style={{ marginBottom: 8 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>Voter reports</h1>
        <p className="muted" style={{ margin: 0, fontSize: 14 }}>
          Registered voters vs the numbers we believe are ours, plus what intelligence officers expect to see.
        </p>
      </header>

      <div className="card" style={{ padding: 12, marginBottom: 12 }}>
        <div className="row-3" style={{ gap: 8 }}>
          <Field label="Scope level">
            <select value={level} onChange={(e) => setLevel(e.target.value as VoterScopeLevel)}>
              <option value="district">District</option>
              <option value="constituency">Constituency</option>
              <option value="ward">Ward</option>
              <option value="branch">Branch</option>
              <option value="pollingStation">Polling station</option>
            </select>
          </Field>
          <Field label="Period">
            <input value={period} onChange={(e) => setPeriod(e.target.value)} placeholder="2026" />
          </Field>
          <span />
        </div>
      </div>

      {counts.error && <ErrorPanel err={counts.error} title="Couldn't load voter counts" />}
      {intel.error && <ErrorPanel err={intel.error} title="Couldn't load voter intel" />}

      <div className="row-3" style={{ gap: 12 }}>
        <SummaryCard label="Total registered" value={totals.registered.toLocaleString()} />
        <SummaryCard label="Total ours" value={`${totals.ours.toLocaleString()} (${totals.pct}%)`} />
        <SummaryCard label="Gap" value={totals.gap.toLocaleString()} />
      </div>

      {provinceAggregates.length > 0 && (
        <div className="card" style={{ marginTop: 12, padding: 16 }}>
          <div className="card-title">Registered vs ours by province</div>
          <p className="muted" style={{ fontSize: 12, margin: "0 0 8px" }}>
            Aggregated from district-level voter counts via the <code>province</code> tag on each district doc.
            "2021" is the ECZ historical baseline; "{period}" is the selected period.
          </p>
          <div style={{ width: "100%", height: 360 }}>
            <ResponsiveContainer>
              <BarChart data={provinceAggregates} margin={{ top: 16, right: 16, left: 0, bottom: 50 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-30} textAnchor="end" interval={0} height={70} fontSize={11} />
                <YAxis fontSize={11} />
                <Tooltip formatter={(v) => Number(v).toLocaleString()} />
                <Legend />
                <Bar dataKey="registered2021" fill="#94a3b8" name="2021 registered" />
                <Bar dataKey="registered" fill="#5b8def" name={`${period} registered`} />
                <Bar dataKey="ours" fill="#34c759" name={`${period} ours`} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {provinceShare.length > 0 && (
        <div className="card" style={{ marginTop: 12, padding: 16 }}>
          <div className="card-title">Registered share by province ({period})</div>
          <p className="muted" style={{ fontSize: 12, margin: "0 0 8px" }}>
            Slice = each province's share of the national registered total for {period}.
          </p>
          <div style={{ width: "100%", height: 380 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={provinceShare}
                  dataKey="value"
                  nameKey="name"
                  outerRadius={140}
                  label={(props) => {
                    const name = (props as { name?: string }).name ?? "";
                    const pct = (props as { percent?: number }).percent ?? 0;
                    return `${name} ${(pct * 100).toFixed(0)}%`;
                  }}
                >
                  {provinceShare.map((entry, i) => (
                    <Cell key={entry.name} fill={PROVINCE_COLORS[i % PROVINCE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => Number(v).toLocaleString()} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="card" style={{ marginTop: 12, padding: 16 }}>
        <div className="card-title">Registered vs ours by {level}</div>
        <p className="muted" style={{ fontSize: 12, margin: "0 0 8px" }}>
          Scroll horizontally to browse all {level}s. Bars: 2021 baseline, {period} registered, {period} ours.
        </p>
        {barData.length === 0 ? (
          <p className="muted" style={{ fontSize: 13 }}>No voter counts at this level for {period}.</p>
        ) : (
          <div style={{ overflowX: "auto", overflowY: "hidden" }}>
            <div style={{ width: Math.max(800, barData.length * 60), height: 380 }}>
              <ResponsiveContainer>
                <BarChart data={barData} margin={{ top: 16, right: 16, left: 0, bottom: 90 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" interval={0} height={110} fontSize={11} />
                  <YAxis fontSize={11} />
                  <Tooltip formatter={(v) => Number(v).toLocaleString()} />
                  <Legend />
                  <Bar dataKey="registered2021" fill="#94a3b8" name="2021 registered" />
                  <Bar dataKey="registered" fill="#5b8def" name={`${period} registered`} />
                  <Bar dataKey="ours" fill="#34c759" name={`${period} ours`} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>

      {confidenceMix.length > 0 && (
        <div className="card" style={{ marginTop: 12, padding: 16 }}>
          <div className="card-title">Intel confidence breakdown</div>
          <div style={{ width: "100%", height: 240 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={confidenceMix} dataKey="value" nameKey="name" outerRadius={80} label>
                  {confidenceMix.map((entry, i) => (
                    <Cell key={entry.name} fill={["#34c759", "#ff9f0a", "#ff3b30"][i % 3]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="card" style={{ padding: 12 }}>
      <div style={{ fontSize: 11, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 600, marginTop: 4 }}>{value}</div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label style={{ display: "block", fontSize: 11, color: "var(--muted)", marginBottom: 2 }}>{label}</label>
      {children}
    </div>
  );
}
